import {Suspense, useEffect, useMemo, useRef} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import {ThreadPreview} from './ThreadPreview'
import {ThreadPreviewSkeleton} from '../ThreadPreviewSkeleton'
import {useCompositeThreadPositioning} from '../../../hooks/use-composite-thread-positioning'
import {useDiffEntries} from '../../../stores/selectors'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CommentsErrorFallback} from './CommentsErrorFallback'
import {CommentsZeroState} from './CommentsZeroState'
import {useSelectedCommentReplyThreadId} from '../../../stores/layout-selectors'

export interface ThreadPreviewsContentProps {
  filteredThreadIds: Set<string>
  onClose: () => void
  isOpen: boolean
  tabSize?: number
  threadPreviews: Thread[]
}

function ThreadPositioningWithStore({threadPreviews}: {threadPreviews: Thread[]}) {
  useCompositeThreadPositioning(threadPreviews, useDiffEntries())
  return null
}

function ThreadPositioningServerOnly({threadPreviews}: {threadPreviews: Thread[]}) {
  useCompositeThreadPositioning(threadPreviews)
  return null
}

/**
 * Container that reads reply-detail state and renders either the thread list or detail.
 * It also hydrates thread positioning for comments that fall outside the current diff.
 */
export function ThreadPreviewsContent({
  filteredThreadIds,
  onClose,
  isOpen,
  tabSize,
  threadPreviews,
}: ThreadPreviewsContentProps) {
  return (
    <>
      {isFeatureEnabled('updated_pull_request_header') ? (
        <ThreadPositioningServerOnly threadPreviews={threadPreviews} />
      ) : (
        <ThreadPositioningWithStore threadPreviews={threadPreviews} />
      )}
      <ThreadPreviewsComponent
        threadPreviews={threadPreviews}
        filteredThreadIds={filteredThreadIds}
        onClose={onClose}
        isOpen={isOpen}
        tabSize={tabSize}
      />
    </>
  )
}

interface ThreadPreviewsComponentProps {
  threadPreviews: Thread[]
  filteredThreadIds: Set<string>
  onClose: () => void
  isOpen: boolean
  tabSize?: number
}

/**
 * Presentational thread previews renderer. It announces result counts, shows zero state
 * for empty filtered results, and switches to a single selected thread in detail mode.
 */
function ThreadPreviewsComponent({
  threadPreviews,
  filteredThreadIds,
  onClose,
  isOpen,
  tabSize,
}: ThreadPreviewsComponentProps) {
  const selectedThreadId = useSelectedCommentReplyThreadId()
  const announceRef = useRef<HTMLDivElement>(null)
  // eslint-disable-next-line @eslint-react/no-unnecessary-use-memo -- need a stable reference to the debounce method
  const debouncedAnnounce = useMemo(() => debounce(announce, 300), [])
  const hasThreads = threadPreviews.length > 0
  const selectedThread = useMemo(
    () => threadPreviews.find(thread => thread.id === selectedThreadId) ?? null,
    [threadPreviews, selectedThreadId],
  )

  const threads = useMemo(
    () => threadPreviews.filter(thread => filteredThreadIds.has(thread?.id)),
    [filteredThreadIds, threadPreviews],
  )

  useEffect(() => {
    if (!isOpen) return

    const element = announceRef.current as HTMLElement

    const threadCommentsLength = selectedThread?.commentsData?.comments?.length
    if (threadCommentsLength) {
      debouncedAnnounce(`${threadCommentsLength} thread comments`, {element})
      return
    }

    const announceText =
      threads.length > 0 ? `${threads.length} ${threads.length === 1 ? 'comment' : 'comments'}` : `No comments found`
    debouncedAnnounce(announceText, {element})
  }, [isOpen, debouncedAnnounce, threads, selectedThread?.commentsData?.comments?.length])

  return (
    <>
      {threads.length > 0 ? (
        <ErrorBoundary boundaryName="DockedThreadPreviewsContent" fallback={<CommentsErrorFallback />}>
          <Suspense fallback={<ThreadPreviewSkeleton />}>
            {selectedThread ? (
              <div className="d-flex flex-column position-relative width-full gap-3">
                <ThreadPreview
                  tabSize={tabSize}
                  thread={selectedThread}
                  onNavigateToDiffComment={onClose}
                  headingLevel="h2"
                />
              </div>
            ) : (
              <div className="d-flex flex-column position-relative width-full gap-3">
                {threads.map(thread => (
                  <ThreadPreview
                    key={thread.id}
                    tabSize={tabSize}
                    thread={thread}
                    onNavigateToDiffComment={onClose}
                    headingLevel="h2"
                  />
                ))}
              </div>
            )}
          </Suspense>
        </ErrorBoundary>
      ) : (
        <CommentsZeroState hasItems={hasThreads} />
      )}
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={announceRef} />
    </>
  )
}
